import { useSyncExternalStore } from "react";
import { get } from "idb-keyval";
import api from "@/lib/api";
import qs from "qs";
import { QUERY_STRING_OPTIONS } from "@/query-rest/common";
import { getJapanAreasCountQueryConfig } from "@/query-rest/japanHotelsRest/japanHotelsRest";
import { getPublicEnv } from "@/utils/env";
import { CONTENT_STATUS } from "@/constants";

interface MinimalHotel {
  documentId: string;
}

interface MinimalArea {
  documentId: string;
  hotels?: MinimalHotel[];
}

const IDB_STORE_KEY = "japan-area-stores";
const IDB_TTL_MS = 3 * 60 * 60 * 1000;

const readCountFromIndexDB = async (): Promise<number | null> => {
  try {
    const stored = await get(IDB_STORE_KEY);
    if (!stored?.value) return null;
    const ageMs = Date.now() - (stored.timestamp ?? 0);
    if (ageMs > IDB_TTL_MS) return null;
    const parsed =
      typeof stored.value === "string"
        ? JSON.parse(stored.value)
        : stored.value;
    type CachedArea = { hotels?: MinimalHotel[] };
    const japanAreas: CachedArea[] | undefined = parsed?.state?.japanAreas;
    if (!japanAreas?.length) return null;
    return japanAreas.flatMap((a) => a.hotels ?? []).length || null;
  } catch {
    return null;
  }
};

const fetchCountFromCMS = async (): Promise<number | null> => {
  try {
    const query = qs.stringify(
      getJapanAreasCountQueryConfig(CONTENT_STATUS.PUBLISHED),
      QUERY_STRING_OPTIONS,
    );
    const url = `${getPublicEnv("NEXT_PUBLIC_STRAPI_API_URL")}/japan-areas?${query}`;
    const response = await api.get(url);
    const areas: MinimalArea[] = (response.data as { data: MinimalArea[] }).data ?? [];
    return areas.flatMap((a) => a.hotels ?? []).length || null;
  } catch {
    return null;
  }
};

// ---------------------------------------------------------------------------
// External store — useSyncExternalStore compatible
//
// React reads _count / _loadingDone synchronously on every render via
// getSnapshot. When the async fetch completes, _notify() triggers all
// subscribed components to re-render with the new value — no useEffect,
// no useState, no render-after-paint delay.
//
// On SPA navigation the module stays in memory, so _count is already set
// and the very first render has the correct value immediately.
// ---------------------------------------------------------------------------
let _count: number | null = null;
let _loadingDone = false;
const _listeners = new Set<() => void>();

const _notify = () => _listeners.forEach((l) => l());

const subscribe = (listener: () => void): (() => void) => {
  _listeners.add(listener);
  return () => {
    _listeners.delete(listener);
  };
};

const getCountSnapshot = () => _count;
const getLoadingSnapshot = () => !_loadingDone;
// Server snapshots — SSR has no IndexDB / no window, always return safe defaults
const getCountServerSnapshot = (): number | null => null;
const getLoadingServerSnapshot = () => true;

// Start the fetch at module load time (client only).
// Any component that imports this module gets the benefit — the fetch is
// already in-flight before the first React render cycle runs.
let _started = false;
const startFetch = () => {
  if (_started) return;
  _started = true;
  (async () => {
    try {
      const indexed = await readCountFromIndexDB();
      if (indexed !== null) {
        _count = indexed;
        _loadingDone = true;
        _notify();
        return;
      }
      const queried = await fetchCountFromCMS();
      _count = queried;
    } catch {
      // silent — store stays null
    } finally {
      _loadingDone = true;
      _notify();
    }
  })();
};

if (typeof window !== "undefined") {
  startFetch();
}

export interface UseHotelCountResult {
  cachedCount: number | null;
  isLoadingCachedCount: boolean;
}

/**
 * Returns the hotel count using useSyncExternalStore — same mechanism as
 * useHasHydrated — so the value is read synchronously on every render including
 * the very first one. No useEffect, no paint-then-update flash.
 *
 * Resolution order (client):
 *   1. IndexDB (japan-area-stores) — warm cache, resolves in ~5-20 ms
 *   2. Minimal REST /japan-areas query — cold cache fallback on first visit
 *
 * Once the main fetchAreas store flow completes, resultText in the parent
 * replaces cachedCount so the final count is always authoritative.
 */
export const useHotelCount = (
  isHotelListPage?: boolean,
): UseHotelCountResult => {
  const count = useSyncExternalStore(
    subscribe,
    getCountSnapshot,
    getCountServerSnapshot,
  );
  const isLoading = useSyncExternalStore(
    subscribe,
    getLoadingSnapshot,
    getLoadingServerSnapshot,
  );

  if (!isHotelListPage) {
    return { cachedCount: null, isLoadingCachedCount: false };
  }

  return { cachedCount: count, isLoadingCachedCount: isLoading };
};
